/*
 * Monitor page: which NecroMancer DEVICE row a phone tile is, and which SLOTS run on it.
 *
 * Nothing here is invented. Three responses the server already sends are joined, all of them
 * already fetched by other V3 pages under the same react-query keys:
 *
 *   GET /uiapi/bm/presence   clients[].devices[].serial   the adb serial a PC reports right now
 *                            clients[].hostname           that PC (installId keeps two PCs apart)
 *   GET /uiapi/devices       [].device_id (= id)          the device row, e.g. "device8"
 *                            [].udid                      the adb serial - the approve path stores
 *                                                         the reported serial here, and the server's
 *                                                         own last-seen stamp joins on exactly
 *                                                         udid == serial (BmPresenceService.recordSeen)
 *                            [].last_seen_pc              the PC that last reported this row's udid
 *                            [].label, [].device_name     friendly names, for the tooltip only
 *   GET /uiapi/slots         [].slot_name, [].device_id   the authoritative slot -> device assignment
 *                                                         (the igAcc projection, not Slot.deviceID)
 *
 * The join is by VALUE, never by position. A deviceId is not a list index: the gasn/ endpoint once
 * resolved phones by where they sat in a list and drove the wrong phone (fixed 8c3bc58). Nothing in
 * this file reads an array index as an identity.
 *
 * THE SAME SERIAL ON TWO PCs
 * The tile key is installId + serial because two PCs can report the same serial (cheap phones ship
 * with "0123456789ABCDEF"; a phone can also be reachable from two PCs). The serial alone decides
 * whenever it is unambiguous. The PC only decides when several device rows carry the same udid:
 * then the row whose last_seen_pc is this tile's PC wins. If the PC cannot single one out - and it
 * often cannot, because the server stamps the SAME last-writer PC onto every row with that udid -
 * the tile says so (state 'ambiguous', every candidate listed) instead of picking one.
 */
import { slotKey, slotNumberOf } from './slotSelection';

/** Query states, as the page derives them from react-query. */
export const LOAD = { LOADING: 'loading', ERROR: 'error', READY: 'ready' };

/** What one tile can say about its phone. */
export const ASSOC = {
  LOADING: 'loading', // /uiapi/devices has not answered yet
  UNAVAILABLE: 'unavailable', // /uiapi/devices failed: we do NOT know, so we must not say "no device row"
  NONE: 'none', // answered, and no device row has this serial as its udid
  MATCHED: 'matched', // exactly one device row
  AMBIGUOUS: 'ambiguous', // several device rows share this serial and the PC cannot tell them apart
};

const norm = (v) => (v == null ? '' : String(v).trim());
// Windows hostnames are case-insensitive; the client reports whatever case the OS gives it.
const normPc = (v) => norm(v).toLowerCase();

/** The device row's id ("device8"). The server sends it twice (id and device_id); both are read. */
export function deviceIdOf(row) {
  if (!row) return '';
  return norm(row.device_id != null && row.device_id !== '' ? row.device_id : row.id);
}

/** Order by the number at the end of the name (slot2 < slot10), name as the tie-break. */
function compareByNumber(a, b) {
  const x = slotNumberOf(a);
  const y = slotNumberOf(b);
  if (x == null && y == null) return String(a).localeCompare(String(b));
  if (x == null) return 1;
  if (y == null) return -1;
  return x - y || String(a).localeCompare(String(b));
}

/** Where a slot's page lives - the same route the Slots page opens for a slot. */
export function slotHref(name) {
  return `/slots/${encodeURIComponent(name)}/settings`;
}

/** Where a device row's page lives - the same route the Devices page opens for a device. */
export function deviceHref(id) {
  return `/devices/${encodeURIComponent(id)}/settings`;
}

/**
 * The device-row and slot lookup tables. This is the heavy half (10,100 slots on the largest
 * account: ~14 ms to build), so the page memoises it on the /uiapi/devices and /uiapi/slots
 * responses ONLY. It must not depend on presence: every 5 s presence poll returns a new object
 * (each PC's `lastSeen` heartbeat moves), and rebuilding this on every poll would put that cost on
 * the main thread every 5 s while up to 48 video streams decode.
 *
 * @param {object} p
 * @param {Array|undefined} p.devices  GET /uiapi/devices rows (undefined while not loaded)
 * @param {Array|undefined} p.slots    GET /uiapi/slots rows (undefined while not loaded)
 * @param {string} p.devicesState      LOAD.* for /uiapi/devices
 * @param {string} p.slotsState        LOAD.* for /uiapi/slots
 */
export function buildRowIndex({ devices, slots, devicesState, slotsState } = {}) {
  // udid -> device rows carrying it. A list, because nothing server-side forbids two rows with
  // the same udid (hand-typed udids, rows from before the approve path refused duplicates).
  const devicesBySerial = new Map();
  if (Array.isArray(devices)) {
    const seen = new Set();
    devices.forEach((row) => {
      const id = deviceIdOf(row);
      const serial = norm(row && row.udid);
      if (!id || !serial || seen.has(id)) return;
      seen.add(id);
      const entry = {
        id,
        label: norm(row.label) || null,
        name: norm(row.device_name) || null,
        lastSeenPc: norm(row.last_seen_pc) || null,
      };
      if (!devicesBySerial.has(serial)) devicesBySerial.set(serial, []);
      devicesBySerial.get(serial).push(entry);
    });
    devicesBySerial.forEach((rows) => rows.sort((a, b) => compareByNumber(a.id, b.id)));
  }

  // device id -> the slots assigned to it, in slot-number order.
  const slotsByDevice = new Map();
  if (Array.isArray(slots)) {
    const seen = new Set();
    slots.forEach((slot) => {
      const deviceId = norm(slot && slot.device_id);
      const name = norm(slotKey(slot));
      if (!deviceId || !name || seen.has(name)) return;
      seen.add(name);
      if (!slotsByDevice.has(deviceId)) slotsByDevice.set(deviceId, []);
      slotsByDevice.get(deviceId).push({
        name,
        label: norm(slot.slot_label) || null,
        username: norm(slot.ig_username) || null,
      });
    });
    slotsByDevice.forEach((list) => list.sort((a, b) => compareByNumber(a.name, b.name)));
  }

  const stateOf = (data, st) => {
    if (Array.isArray(data)) return LOAD.READY; // a later failed refetch keeps the good rows
    // A failure, or an answer that is not the bare array the contract promises (an HTML error
    // page, null): either way the rows are unknown, which is not the same as "none".
    if (st === LOAD.ERROR || st === LOAD.READY || (data !== undefined && data !== null)) return LOAD.ERROR;
    return LOAD.LOADING;
  };

  return {
    devicesBySerial,
    slotsByDevice,
    devicesState: stateOf(devices, devicesState),
    slotsState: stateOf(slots, slotsState),
  };
}

/**
 * serial -> every PC reporting it right now, so a tile can say "this serial is also on PC-B".
 * The cheap half (one pass over the phones on the account), rebuilt on each presence poll.
 *
 * @param {Array} phones  flattened presence phones: { serial, installId, pc }
 * @returns {Map<string, Array<{installId:*, pc:?string}>>}
 */
export function buildPresenceIndex(phones) {
  const pcsBySerial = new Map();
  (Array.isArray(phones) ? phones : []).forEach((p) => {
    const serial = norm(p && p.serial);
    if (!serial) return;
    if (!pcsBySerial.has(serial)) pcsBySerial.set(serial, []);
    const list = pcsBySerial.get(serial);
    if (!list.some((e) => e.installId === p.installId)) {
      list.push({ installId: p.installId, pc: norm(p.pc) || null });
    }
  });
  return pcsBySerial;
}

/**
 * Everything a tile reads, in one object: the row index plus the presence index. The page builds
 * the two halves under separate memos (see buildRowIndex) and joins them with this same shape.
 *
 * @param {object} p  buildRowIndex's fields plus `phones` (flattened presence phones)
 */
export function buildMonitorIndex({ devices, slots, phones, devicesState, slotsState } = {}) {
  return {
    ...buildRowIndex({ devices, slots, devicesState, slotsState }),
    pcsBySerial: buildPresenceIndex(phones),
  };
}

/**
 * What one tile shows.
 *
 * @param {{serial:string, installId:*, pc:?string}} phone  one presence phone
 * @param {object} index  from buildMonitorIndex
 * @returns {{
 *   state: string,                 ASSOC.*
 *   devices: Array<{id,label,name,lastSeenPc}>,  the row (MATCHED) or every candidate (AMBIGUOUS)
 *   slots: Array<{name,label,username}>,         slots on those rows, slot-number order
 *   slotsState: string,            LOAD.* for /uiapi/slots
 *   otherPcs: string[],            other PCs reporting this same serial right now
 * }}
 */
export function associatePhone(phone, index) {
  const serial = norm(phone && phone.serial);
  const installId = phone ? phone.installId : undefined;
  const otherPcs = ((index && index.pcsBySerial.get(serial)) || [])
    .filter((e) => e.installId !== installId)
    .map((e) => e.pc || 'another PC');
  const base = { devices: [], slots: [], slotsState: index ? index.slotsState : LOAD.LOADING, otherPcs };

  if (!index || index.devicesState === LOAD.LOADING) return { ...base, state: ASSOC.LOADING };
  if (index.devicesState === LOAD.ERROR) return { ...base, state: ASSOC.UNAVAILABLE };

  let rows = serial ? index.devicesBySerial.get(serial) || [] : [];
  if (rows.length === 0) return { ...base, state: ASSOC.NONE };

  let state = ASSOC.MATCHED;
  if (rows.length > 1) {
    const pc = normPc(phone && phone.pc);
    const onThisPc = pc ? rows.filter((r) => normPc(r.lastSeenPc) === pc) : [];
    if (onThisPc.length === 1) rows = onThisPc;
    else state = ASSOC.AMBIGUOUS;
  }

  // For an ambiguous phone the slots of every candidate are listed: each of those rows addresses
  // the phone by this same serial, so each of their slots does drive a phone with it.
  const slots = [];
  if (index.slotsState === LOAD.READY) {
    const seen = new Set();
    rows.forEach((r) => (index.slotsByDevice.get(r.id) || []).forEach((s) => {
      if (!seen.has(s.name)) { seen.add(s.name); slots.push(s); }
    }));
    if (rows.length > 1) slots.sort((a, b) => compareByNumber(a.name, b.name));
  }

  return { ...base, state, devices: rows, slots };
}
